import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { reportService, Report, ReportStatus, ReportStatusValue, REPORT_REASON_LABELS, ModerationAction, ModerationActionValue, MODERATION_ACTION_LABELS } from '../services/reportService';
import { ModerationActionDialog, UserHistoryDialog } from './AdminModerationDialogs';

const STATUS_COLOR: Record<ReportStatusValue, 'warning' | 'success' | 'default'> = {
  Open: 'warning',
  Resolved: 'success',
  Dismissed: 'default',
};

/**
 * Reports queue for admins (User.IsAdmin — the same gate AdminPanel's own routes already
 * require, checked server-side by ReportsController). A report against a post, comment or region event
 * carries its own snapshot of the target's author/text, so a since-deleted or edited item
 * still shows what was actually reported.
 */
const AdminReportsTab: React.FC = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ReportStatusValue>(ReportStatus.Open);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<{ report: Report; action: ModerationActionValue } | null>(null);
  const [historyUserId, setHistoryUserId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReports(await reportService.getReports());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleResolve = async (reportId: string, status: ReportStatusValue) => {
    setActionLoadingId(reportId);
    try {
      const updated = await reportService.resolveReport(reportId, status);
      setReports(prev => prev.map(r => (r.reportId === reportId ? updated : r)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update report');
    } finally {
      setActionLoadingId(null);
    }
  };

  const filtered = reports.filter(r => r.status === statusFilter);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <ToggleButtonGroup
        size="small"
        value={statusFilter}
        exclusive
        onChange={(_, v) => v && setStatusFilter(v)}
        sx={{ mb: 2 }}
      >
        <ToggleButton value={ReportStatus.Open}>Open</ToggleButton>
        <ToggleButton value={ReportStatus.Resolved}>Resolved</ToggleButton>
        <ToggleButton value={ReportStatus.Dismissed}>Dismissed</ToggleButton>
      </ToggleButtonGroup>

      {filtered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">No {statusFilter.toLowerCase()} reports</Typography>
        </Box>
      )}

      {filtered.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reported</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Content</TableCell>
                <TableCell>Reporter</TableCell>
                <TableCell align="center">Status</TableCell>
                {statusFilter === ReportStatus.Open && <TableCell align="center">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(report => (
                <TableRow key={report.reportId} hover>
                  <TableCell>{new Date(report.createdAt).toLocaleString()}</TableCell>
                  <TableCell>{REPORT_REASON_LABELS[report.reason]}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        // An event report has no post page to open; its text snapshot is the evidence.
                        ...(report.targetType !== 'Event' && { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }),
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                      onClick={report.targetType === 'Event' ? undefined : () => navigate(`/post/${report.postId}`)}
                    >
                      {report.targetType === 'Comment' ? 'Comment: ' : report.targetType === 'Event' ? 'Event: ' : ''}{report.targetTextSnapshot || '(no text)'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      by{' '}
                      <Box
                        component="span"
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => setHistoryUserId(report.targetAuthorUserId)}
                      >
                        {report.targetAuthorDisplayName}
                      </Box>
                    </Typography>
                    {report.details && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Note: {report.details}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{report.reporterDisplayName}</TableCell>
                  <TableCell align="center">
                    <Chip label={report.status} color={STATUS_COLOR[report.status]} size="small" />
                    {report.actionTaken && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {MODERATION_ACTION_LABELS[report.actionTaken]}
                      </Typography>
                    )}
                  </TableCell>
                  {statusFilter === ReportStatus.Open && (
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                        <Button size="small" color="error" variant="contained" onClick={() => setPendingAction({ report, action: ModerationAction.RemoveContent })}>
                          Remove
                        </Button>
                        <Button size="small" color="warning" variant="outlined" onClick={() => setPendingAction({ report, action: ModerationAction.WarnUser })}>
                          Warn
                        </Button>
                        <Button size="small" color="error" variant="outlined" onClick={() => setPendingAction({ report, action: ModerationAction.SuspendUser })}>
                          Suspend
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={actionLoadingId === report.reportId}
                          onClick={() => handleResolve(report.reportId, ReportStatus.Resolved)}
                        >
                          Resolve
                        </Button>
                        <Button
                          size="small"
                          color="inherit"
                          disabled={actionLoadingId === report.reportId}
                          onClick={() => handleResolve(report.reportId, ReportStatus.Dismissed)}
                        >
                          Dismiss
                        </Button>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pendingAction && (
        <ModerationActionDialog
          report={pendingAction.report}
          action={pendingAction.action}
          onClose={() => setPendingAction(null)}
          onDone={() => { setPendingAction(null); load(); }}
        />
      )}
      {historyUserId && <UserHistoryDialog userId={historyUserId} onClose={() => setHistoryUserId(null)} />}
    </Box>
  );
};

export default AdminReportsTab;
